import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/* ==========================================================================
   Utilidades
   ========================================================================== */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

const money = (cents) => {
  const format = window.PitterPan?.moneyFormat ?? 'R$ {{amount_with_comma_separator}}';
  const currency = window.Shopify?.currency?.active ?? 'BRL';
  return formatMoney(cents, format, currency);
};

const strings = () => window.PitterPan?.strings ?? {};

/** Ids das seções que o carrinho do Horizon precisa re-renderizar. */
const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

/**
 * Adiciona uma variante ao carrinho e avisa o Horizon (bolha do carrinho,
 * gaveta do carrinho) pelo mesmo evento que o formulário de produto usa.
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {Element} source
 */
export async function addToCart(variantId, quantity, source) {
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity));
  const sections = cartSectionIds();
  if (sections.length) body.append('sections', sections.join(','));

  const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });
  const result = await response.json();

  if (result.status) {
    const error = new Error(result.description || result.message);
    throw error;
  }

  const cart = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`).then((r) => r.json());

  source.dispatchEvent(
    new CartAddEvent(cart, String(variantId), {
      source: 'product-form-component',
      itemCount: quantity,
      productId: String(result.product_id ?? ''),
      sections: result.sections,
    })
  );

  openCartDrawer();
  return result;
}

export function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) drawer.open();
}

/* ==========================================================================
   Quantidade
   ========================================================================== */

class PpQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', this.#onClick);
    this.input?.addEventListener('change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.input?.removeEventListener('change', this.#onChange);
  }

  get value() {
    return Math.max(this.min, Number(this.input?.value) || this.min);
  }

  set value(v) {
    if (!this.input) return;
    this.input.value = String(this.#clamp(v));
    this.#sync();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  get min() {
    return Number(this.input?.min) || 1;
  }

  get max() {
    return Number(this.input?.max) || Infinity;
  }

  #clamp(v) {
    return Math.min(this.max, Math.max(this.min, Math.round(Number(v) || this.min)));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (!btn || !this.input) return;
    this.value = this.value + Number(btn.getAttribute('data-step'));
  };

  #onChange = () => {
    if (!this.input) return;
    const clamped = this.#clamp(this.input.value);
    if (String(clamped) !== this.input.value) this.input.value = String(clamped);
    this.#sync();
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.quantitySelectorUpdate, { bubbles: true, detail: { quantity: clamped } })
    );
  };

  #sync() {
    const [minus, plus] = this.querySelectorAll('[data-step]');
    if (minus instanceof HTMLButtonElement) minus.disabled = this.value <= this.min;
    if (plus instanceof HTMLButtonElement) plus.disabled = this.value >= this.max;
  }
}
define('pp-qty', PpQty);

/* ==========================================================================
   Compra rápida no card
   ========================================================================== */

class PpCardBuy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add]');
    this.button?.addEventListener('click', this.#onAdd);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onAdd);
  }

  #onAdd = async () => {
    const button = this.button;
    if (!(button instanceof HTMLButtonElement) || button.classList.contains('is-loading')) return;
    const qty = this.querySelector('pp-qty');
    const quantity = qty instanceof PpQty ? qty.value : 1;

    this.querySelector('.pp-card__error')?.remove();
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      await addToCart(this.dataset.variantId ?? '', quantity, this);
      if (qty instanceof PpQty) qty.value = 1;
    } catch (error) {
      const message = document.createElement('p');
      message.className = 'pp-card__error';
      message.textContent = error instanceof Error ? error.message : String(error);
      this.after(message);
      setTimeout(() => message.remove(), 5000);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };
}
define('pp-card-buy', PpCardBuy);

/* ==========================================================================
   Carrossel (scroll-snap) com setas, autoplay e loop
   ========================================================================== */

class PpCarousel extends HTMLElement {
  #timer = 0;
  #paused = false;

  connectedCallback() {
    this.track = this.querySelector('.pp-carousel__track');
    if (!this.track) return;

    const navScope = this.closest('.shopify-section') ?? this;
    this.prev = navScope.querySelector(`[data-carousel-prev="${this.id}"]`);
    this.next = navScope.querySelector(`[data-carousel-next="${this.id}"]`);
    this.loop = this.hasAttribute('loop');

    this.prev?.addEventListener('click', this.#onPrev);
    this.next?.addEventListener('click', this.#onNext);
    this.track.addEventListener('scroll', this.#updateButtons, { passive: true });
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#updateButtons();
    this.#start();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.prev?.removeEventListener('click', this.#onPrev);
    this.next?.removeEventListener('click', this.#onNext);
  }

  get #page() {
    return this.track ? this.track.clientWidth : 0;
  }

  get #atEnd() {
    if (!this.track) return true;
    return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
  }

  get #atStart() {
    return !this.track || this.track.scrollLeft <= 4;
  }

  #onPrev = () => {
    if (!this.track) return;
    if (this.#atStart && this.loop) this.track.scrollTo({ left: this.track.scrollWidth });
    else this.track.scrollBy({ left: -this.#page });
  };

  #onNext = () => {
    if (!this.track) return;
    if (this.#atEnd && this.loop) this.track.scrollTo({ left: 0 });
    else this.track.scrollBy({ left: this.#page });
  };

  #updateButtons = () => {
    if (this.loop) return;
    if (this.prev instanceof HTMLButtonElement) this.prev.disabled = this.#atStart;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = this.#atEnd;
  };

  #start() {
    const seconds = Number(this.getAttribute('autoplay'));
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#timer = window.setInterval(() => {
      if (this.#paused || document.hidden) return;
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      this.#onNext();
    }, seconds * 1000);
  }

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };
}
define('pp-carousel', PpCarousel);

/* ==========================================================================
   Slideshow
   ========================================================================== */

class PpSlideshow extends HTMLElement {
  #index = 0;
  #timer = 0;
  #startX = 0;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('.pp-slide')];
    this.dots = [...this.querySelectorAll('[data-slide-to]')];
    if (this.slides.length < 2) return;

    this.dots.forEach((dot) => dot.addEventListener('click', this.#onDot));
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointerenter', () => this.hasAttribute('pause-on-hover') && this.#stop());
    this.addEventListener('pointerleave', () => this.hasAttribute('pause-on-hover') && this.#play());
    this.#go(0);
    this.#play();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #play() {
    this.#stop();
    const seconds = Number(this.getAttribute('autoplay'));
    if (!seconds) return;
    this.#timer = window.setInterval(() => {
      if (!document.hidden) this.#go(this.#index + 1);
    }, seconds * 1000);
  }

  #stop() {
    clearInterval(this.#timer);
  }

  /** @param {number} index */
  #go(index) {
    const total = this.slides.length;
    this.#index = ((index % total) + total) % total;
    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', active ? '0' : '-1'));
      slide.querySelectorAll('video').forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        if (active && video.offsetParent !== null) video.play().catch(() => {});
        else video.pause();
      });
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.#index)));
  }

  /** @param {Event} event */
  #onDot = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof Element)) return;
    this.#go(Number(target.getAttribute('data-slide-to')));
    this.#play();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#startX = event.clientX;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const delta = event.clientX - this.#startX;
    if (Math.abs(delta) < 40) return;
    this.#go(this.#index + (delta < 0 ? 1 : -1));
    this.#play();
  };
}
define('pp-slideshow', PpSlideshow);

/* ==========================================================================
   Header: sticky compacto, mega menu, menu mobile, busca
   ========================================================================== */

class PpHeader extends HTMLElement {
  #lastY = 0;
  #ticking = false;
  #hoverTimer = 0;

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.sticky = this.hasAttribute('sticky');

    if (this.sticky) {
      this.#lockHeight();
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#lockHeight);
      window.addEventListener('load', this.#lockHeight);
      this.querySelectorAll('img').forEach((img) => img.addEventListener('load', this.#lockHeight, { once: true }));
    }

    this.querySelectorAll('.pp-nav__item--mega').forEach((item) => {
      item.addEventListener('pointerenter', () => this.#openMega(item));
      item.addEventListener('pointerleave', () => this.#closeMega(item, true));
      item.addEventListener('focusin', () => this.#openMega(item));
      item.addEventListener('focusout', (event) => {
        if (!(event.relatedTarget instanceof Node) || !item.contains(event.relatedTarget)) this.#closeMega(item);
      });
    });
    window.addEventListener('resize', this.#repositionOpenMega);

    this.querySelectorAll('.pp-mega__parent').forEach((parent) => {
      if (parent.matches('button[data-panel]')) {
        parent.addEventListener('click', () => this.#selectMegaParent(parent));
      }
    });

    this.querySelectorAll('.pp-mega__scroll-hint').forEach((hint) => {
      const list = hint.previousElementSibling;
      if (!list) return;
      const update = () => hint.toggleAttribute('hidden', list.scrollTop + list.clientHeight >= list.scrollHeight - 4);
      list.addEventListener('scroll', update, { passive: true });
      hint.addEventListener('click', () => list.scrollBy({ top: 120, behavior: 'smooth' }));
      update();
    });

    this.drawer = this.querySelector('.pp-drawer');
    this.querySelectorAll('[data-drawer-open]').forEach((b) => b.addEventListener('click', this.#openDrawer));
    this.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', this.#closeDrawer));
    this.querySelectorAll('[data-drill]').forEach((b) =>
      b.addEventListener('click', () => this.#drill(b.getAttribute('data-drill')))
    );
    this.querySelectorAll('[data-drill-back]').forEach((b) =>
      b.addEventListener('click', () => this.#drill(b.getAttribute('data-drill-back')))
    );
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#lockHeight);
    window.removeEventListener('resize', this.#repositionOpenMega);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #lockHeight = () => {
    if (!this.section || this.classList.contains('is-scrolled')) return;
    this.section.style.height = '';
    this.section.style.height = `${this.offsetHeight}px`;
    document.documentElement.style.setProperty('--pp-header-height', `${this.offsetHeight}px`);
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      const y = window.scrollY;
      const threshold = (this.section?.offsetHeight ?? 200) + 40;
      const down = y > this.#lastY;

      if (y <= 4) {
        this.classList.remove('is-scrolled', 'is-out', 'is-pinned');
      } else if (y > threshold) {
        this.classList.add('is-scrolled');
        if (Math.abs(y - this.#lastY) > 6) {
          this.classList.toggle('is-out', down && !this.#menuOpen);
          this.classList.toggle('is-pinned', !down || this.#menuOpen);
        }
      } else if (!this.classList.contains('is-pinned')) {
        this.classList.remove('is-scrolled', 'is-out');
      } else if (down) {
        this.classList.add('is-out');
      } else {
        this.classList.remove('is-out');
      }

      this.#lastY = y;
    });
  };

  get #menuOpen() {
    return Boolean(this.querySelector('.pp-nav__item--mega.is-open')) || this.classList.contains('drawer-open');
  }

  /** @param {Element} item */
  #openMega(item) {
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      this.querySelectorAll('.pp-nav__item--mega.is-open').forEach((o) => o !== item && this.#closeMega(o, true));
      if (!item.classList.contains('is-open')) this.#resetMega(item);
      this.#positionMega(item);
      item.classList.add('is-open');
      item.querySelector('.pp-nav__link')?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('pp-mega-open');
    }, 80);
  }

  /** @param {Element} item */
  #positionMega(item) {
    const mega = item.querySelector('.pp-mega');
    const link = item.querySelector('.pp-nav__link');
    if (!mega || !link) return;

    const menuWidth = mega.getBoundingClientRect().width;
    const triggerLeft = link.getBoundingClientRect().left - mega.getBoundingClientRect().left;
    const columnWidth = 270;
    let left = Math.min(Math.max(triggerLeft, 0), Math.max(menuWidth - columnWidth, 0));
    const needsMoreSpace = left + columnWidth * 2 > menuWidth;
    const flipped = needsMoreSpace && left >= columnWidth;
    if (needsMoreSpace && !flipped) left = Math.max(menuWidth - columnWidth * 2, 0);

    mega.style.setProperty('--pp-mega-left', `${left}px`);
    mega.classList.toggle('is-flipped', flipped);
  }

  #repositionOpenMega = () => {
    const item = this.querySelector('.pp-nav__item--mega.is-open');
    if (item) this.#positionMega(item);
  };

  /**
   * @param {Element} item
   * @param {boolean} [instant]
   */
  #closeMega(item, instant = false) {
    clearTimeout(this.#hoverTimer);
    const close = () => {
      item.classList.remove('is-open');
      item.querySelector('.pp-nav__link')?.setAttribute('aria-expanded', 'false');
      if (!this.querySelector('.pp-nav__item--mega.is-open')) document.body.classList.remove('pp-mega-open');
    };
    if (instant) close();
    else this.#hoverTimer = window.setTimeout(close, 120);
  }

  /** @param {Element} parent */
  #selectMegaParent(parent) {
    const mega = parent.closest('.pp-mega');
    if (!mega) return;
    mega.classList.add('has-selection');
    mega.querySelectorAll('.pp-mega__parent').forEach((p) => p.classList.toggle('is-active', p === parent));
    mega.querySelectorAll('button.pp-mega__parent').forEach((p) => p.setAttribute('aria-expanded', String(p === parent)));
    const target = parent.getAttribute('data-panel');
    mega.querySelectorAll('.pp-mega__panel').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.id !== target);
    });
  }

  /** @param {Element} item */
  #resetMega(item) {
    const mega = item.querySelector('.pp-mega');
    if (!mega) return;
    mega.classList.remove('has-selection');
    mega.querySelectorAll('.pp-mega__parent').forEach((parent) => {
      parent.classList.remove('is-active');
      if (parent.matches('button')) parent.setAttribute('aria-expanded', 'false');
    });
    mega.querySelectorAll('.pp-mega__panel').forEach((panel) => panel.setAttribute('hidden', ''));
  }

  #openDrawer = () => {
    if (!this.drawer) return;
    this.drawer.removeAttribute('hidden');
    requestAnimationFrame(() => this.drawer?.classList.add('is-open'));
    this.classList.add('drawer-open');
    document.body.classList.add('pp-drawer-lock');
    this.#drill('root');
  };

  #closeDrawer = () => {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.classList.remove('drawer-open');
    document.body.classList.remove('pp-drawer-lock');
    window.setTimeout(() => this.drawer?.setAttribute('hidden', ''), 300);
  };

  /** @param {string | null} panelId */
  #drill(panelId) {
    if (!this.drawer || !panelId) return;
    this.drawer.querySelectorAll('.pp-drawer__panel').forEach((panel) => {
      panel.classList.toggle('is-active', panel.getAttribute('data-panel-id') === panelId);
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.classList.contains('drawer-open')) this.#closeDrawer();
    this.querySelectorAll('.pp-nav__item--mega.is-open').forEach((item) => this.#closeMega(item, true));
  };
}
define('pp-header', PpHeader);

/* ==========================================================================
   Busca com placeholder animado e sugestões
   ========================================================================== */

class PpSearch extends HTMLElement {
  #timer = 0;
  #typing = 0;
  #abort = /** @type {AbortController | null} */ (null);

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.pp-search__results');
    this.reset = this.querySelector('.pp-search__reset');
    if (!this.input) return;

    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('focus', this.#onFocus);
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#hide();
    });
    this.reset?.addEventListener('click', this.#onReset);
    this.results?.addEventListener('click', this.#onResultsClick);
    document.addEventListener('click', this.#onDocClick);
    window.addEventListener('resize', this.#place);
    this.#syncValue();

    const prompts = (this.dataset.prompts ?? '').split('|').map((p) => p.trim()).filter(Boolean);
    if (prompts.length) this.#animatePlaceholder(prompts);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
    window.removeEventListener('resize', this.#place);
    clearTimeout(this.#typing);
  }

  /** @param {string[]} prompts */
  #animatePlaceholder(prompts) {
    if (!this.input || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (this.input) this.input.placeholder = prompts[0] ?? '';
      return;
    }
    const input = this.input;
    // prefixo comum (ex.: "O que você procura? ") fica fixo; só o resto digita e apaga
    let keep = 0;
    const first = prompts[0] ?? '';
    while (keep < first.length && prompts.every((t) => t[keep] === first[keep])) keep += 1;
    keep = Math.max(0, first.lastIndexOf(' ', Math.max(0, keep - 1)) + 1 || keep);
    if (prompts.length < 2) keep = 0;
    let p = 0;
    let c = keep;
    let deleting = false;

    const tick = () => {
      const text = prompts[p] ?? '';
      if (document.activeElement === input) {
        // com foco: frase completa e estática (nada de texto cortado)
        input.placeholder = text;
        c = text.length;
        deleting = false;
        this.#typing = window.setTimeout(tick, 400);
        return;
      }
      if (!input.value) {
        c += deleting ? -1 : 1;
        input.placeholder = text.slice(0, c);
      }
      let delay = deleting ? 35 : 75;
      if (!deleting && c >= text.length) {
        deleting = true;
        delay = 1800;
      } else if (deleting && c <= keep) {
        deleting = false;
        p = (p + 1) % prompts.length;
        delay = 400;
      }
      this.#typing = window.setTimeout(tick, delay);
    };
    tick();
  }

  #syncValue() {
    this.classList.toggle('has-value', Boolean(this.input?.value));
  }

  /** O painel ocupa a largura inteira da tela, alinhado à borda esquerda. */
  #place = () => {};

  #onFocus = () => {
    if (this.input && this.input.value.trim().length > 1 && this.results?.innerHTML.trim()) this.#show();
  };

  #onInput = () => {
    this.#syncValue();
    clearTimeout(this.#timer);
    const q = this.input?.value.trim() ?? '';
    if (q.length < 2) {
      this.#abort?.abort();
      this.#hide();
      return;
    }
    this.#timer = window.setTimeout(() => this.#fetch(q), 250);
  };

  #onReset = () => {
    if (!this.input) return;
    this.input.value = '';
    this.#syncValue();
    this.#abort?.abort();
    this.#hide();
    this.input.focus();
  };

  /** @param {MouseEvent} event */
  #onResultsClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-tab]') : null;
    if (!tab || !this.results) return;
    const name = tab.getAttribute('data-tab');
    this.results.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    this.results.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.getAttribute('data-panel') !== name);
    });
  };

  /** @param {string} q */
  async #fetch(q) {
    if (!this.results) return;
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.classList.add('is-loading');
    if (this.results.innerHTML.trim()) this.#show();
    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,page,article,query&resources[limit]=8&resources[options][unavailable_products]=last&section_id=pp-predictive-search`;
    try {
      const html = await fetch(url, { signal: this.#abort.signal }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.pp-predictive');
      this.results.innerHTML = content ? content.outerHTML : '';
      if (content) this.#show();
      else this.#hide();
      this.classList.remove('is-loading');
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.classList.remove('is-loading');
        this.#hide();
      }
    }
  }

  #show() {
    this.#place();
    this.classList.add('is-open');
    document.body.classList.add('pp-search-open');
  }

  #hide() {
    this.classList.remove('is-open', 'is-loading');
    document.body.classList.remove('pp-search-open');
  }

  /** @param {MouseEvent} event */
  #onDocClick = (event) => {
    if (!this.classList.contains('is-open')) return;
    const target = event.target;
    if (target instanceof Node && !this.contains(target)) this.#hide();
  };
}
define('pp-search', PpSearch);

/* ==========================================================================
   Parcelamento sem juros
   ========================================================================== */

/**
 * Parcelas sem juros: quantidade = piso(total / parcela mínima), limitada ao máximo
 * configurado e nunca menor que 1 (mesma regra do tema anterior).
 */
class InstallmentsText extends HTMLElement {
  /** @param {number} total Total em centavos (preço unitário x quantidade). */
  render(total) {
    const { minAmount, maxCount, template } = this.dataset;
    const min = Number(minAmount);
    const max = Number(maxCount);
    if (!total || !template || !(min > 0) || !(max > 0)) return;
    const count = Math.max(1, Math.min(Math.floor(total / min), max));
    this.innerHTML = template.replace('[count]', String(count)).replace('[price]', money(Math.round(total / count)));
  }
}
define('installments-text', InstallmentsText);

/**
 * Prévia do total conforme a quantidade: atualiza o preço (e o preço antigo) e as
 * parcelas de tudo que está no mesmo escopo [data-installments-scope] do seletor.
 * O preço unitário vem de [data-unit-price] (em centavos).
 * @param {Element} scope
 */
export function updateQuantityPreview(scope) {
  const qtyEl = scope.querySelector('pp-qty');
  const quantity = qtyEl instanceof PpQty ? qtyEl.value : 1;
  let total = 0;

  scope.querySelectorAll('[data-unit-price]').forEach((box) => {
    const unit = Number(box.getAttribute('data-unit-price'));
    if (!unit) return;
    total = unit * quantity;
    const current = box.querySelector('[data-price], .pp-price__current');
    if (current) {
      current.textContent = money(total);
      current.classList.remove('is-updating');
      void /** @type {HTMLElement} */ (current).offsetWidth; // reinicia a animação
      current.classList.add('is-updating');
    }
    const compareUnit = Number(box.getAttribute('data-unit-compare'));
    const compare = box.querySelector('[data-compare-price], .pp-price__compare');
    if (compare && compareUnit > unit) compare.textContent = money(compareUnit * quantity);
  });

  if (total) scope.querySelectorAll('installments-text').forEach((el) => el instanceof InstallmentsText && el.render(total));
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  if (/** @type {any} */ (event).detail?.cartLine) return;
  const scope = event.target instanceof Element ? event.target.closest('[data-installments-scope]') : null;
  if (!scope) return;
  updateQuantityPreview(scope);

  // produto e barra fixa compartilham a mesma quantidade
  const productId = scope instanceof PpProduct ? scope.id : scope.getAttribute('data-sticky-for');
  if (!productId) return;
  const source = scope.querySelector('pp-qty input');
  const other = scope instanceof PpProduct ? document.querySelector(`[data-sticky-for="${productId}"]`) : document.getElementById(productId);
  const target = other?.querySelector('pp-qty input');
  if (source instanceof HTMLInputElement && target instanceof HTMLInputElement && target.value !== source.value) {
    target.value = source.value;
    if (other) updateQuantityPreview(other);
  }
});

/* ==========================================================================
   Carrinho lateral: frete e instruções especiais
   ========================================================================== */

/**
 * Os botões ficam lado a lado e o painel aberto ocupa a largura toda logo abaixo;
 * abrir um fecha o outro. Ouvinte no document porque o carrinho é re-renderizado.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-cart-tool]') : null;
  const tools = toggle?.closest('.pp-cart-tools');
  if (!toggle || !tools) return;
  const open = toggle.getAttribute('aria-expanded') !== 'true';
  tools.querySelectorAll('[data-cart-tool]').forEach((btn) => {
    const active = btn === toggle && open;
    btn.setAttribute('aria-expanded', String(active));
    const panel = document.getElementById(btn.getAttribute('aria-controls') ?? '');
    if (panel) panel.hidden = !active;
  });
  if (!open) return;
  const field = document.getElementById(toggle.getAttribute('aria-controls') ?? '')?.querySelector('input:not([type="hidden"]), textarea');
  if (field instanceof HTMLElement) field.focus({ preventScroll: true });
});

/* ==========================================================================
   Calculadora de frete (CEP)
   ========================================================================== */

const BRAZILIAN_STATES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
  DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão',
  MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará',
  PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima',
  SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
};

/**
 * Calcula o frete pelo CEP. Na página de produto (atributo `product`) o item
 * é adicionado temporariamente ao carrinho para obter as tarifas, e o
 * carrinho é restaurado em seguida. No carrinho, usa o conteúdo atual.
 */
class PpShippingCalc extends HTMLElement {
  #loading = false;

  connectedCallback() {
    this.input = this.querySelector('input');
    this.button = this.querySelector('button[data-calc]');
    this.results = this.querySelector('.pp-ship__results');
    this.input?.addEventListener('input', this.#onInput);
    this.input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (this.button instanceof HTMLButtonElement && !this.button.disabled) this.#calculate();
    });
    this.button?.addEventListener('click', () => this.#calculate());
    const saved = sessionStorage.getItem('pp-cep');
    if (saved && this.input instanceof HTMLInputElement && !this.input.value) {
      this.input.value = saved;
      this.#onInput();
    }
  }

  #digits() {
    return this.input instanceof HTMLInputElement ? this.input.value.replace(/\D/g, '').slice(0, 8) : '';
  }

  #onInput = () => {
    if (!(this.input instanceof HTMLInputElement)) return;
    const d = this.#digits();
    this.input.value = d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    if (this.button instanceof HTMLButtonElement) this.button.disabled = this.#loading || d.length !== 8;
  };

  #variantId() {
    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    const input = form?.querySelector('[name="id"]');
    return input instanceof HTMLInputElement || input instanceof HTMLSelectElement ? input.value : null;
  }

  #quantity() {
    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    const input =
      form?.querySelector('[name="quantity"]') ??
      (this.dataset.formId ? document.querySelector(`[form="${this.dataset.formId}"][name="quantity"]`) : null);
    const v = input instanceof HTMLInputElement ? parseInt(input.value, 10) : 1;
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  /**
   * @param {string} zip
   * @param {string} province
   */
  async #rates(zip, province) {
    const root = window.Shopify?.routes?.root ?? '/';
    const params = `shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=Brazil&shipping_address[province]=${encodeURIComponent(province)}`;
    const get = async () => {
      const response = await fetch(`${root}cart/shipping_rates.json?${params}`);
      const data = await response.json();
      if (!response.ok) throw data;
      return data.shipping_rates;
    };

    if (!this.hasAttribute('product')) return get();

    const variantId = this.#variantId();
    if (!variantId) throw new Error('no-variant');
    const quantity = this.#quantity();
    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    }).then((r) => r.json());
    if (added.status) throw added;
    const original = Math.max(0, (added.quantity || quantity) - quantity);
    try {
      return await get();
    } finally {
      await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: added.key, quantity: original }),
      });
    }
  }

  async #calculate() {
    const s = strings().shipping ?? {};
    const d = this.#digits();
    if (d.length !== 8) return this.#message(s.invalidZip, true);

    this.#setLoading(true);
    this.#spinner(s.calculating);
    try {
      const address = await fetch(`https://viacep.com.br/ws/${d}/json/`).then((r) => r.json());
      if (!address || address.erro) return this.#message(s.invalidZip, true);
      sessionStorage.setItem('pp-cep', d);
      const location = `${address.localidade} - ${address.uf}`;
      this.#spinner((s.calculatingFor ?? '').replace('[location]', location));
      const rates = await this.#rates(d, BRAZILIAN_STATES[address.uf] ?? address.uf);
      this.#show(rates, location);
    } catch {
      this.#message(s.error, true);
    } finally {
      this.#setLoading(false);
    }
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.#loading = loading;
    this.button?.classList.toggle('is-loading', loading);
    if (this.button instanceof HTMLButtonElement) this.button.disabled = loading || this.#digits().length !== 8;
  }

  /** @param {string} text */
  #spinner(text) {
    if (!this.results) return;
    this.results.classList.remove('is-error');
    this.results.innerHTML = `<p class="pp-ship__loading"><span class="pp-ship__spinner"></span>${text ?? ''}</p>`;
    this.results.hidden = false;
  }

  /**
   * @param {string} text
   * @param {boolean} error
   */
  #message(text, error) {
    if (!this.results) return;
    this.results.classList.toggle('is-error', error);
    this.results.innerHTML = `<p>${text ?? ''}</p>`;
    this.results.hidden = false;
  }

  /**
   * @param {Array<{name: string, price: string, delivery_days?: number[]}>} rates
   * @param {string} location
   */
  #show(rates, location) {
    const s = strings().shipping ?? {};
    if (!rates || !rates.length) return this.#message(s.noResults, true);
    const rows = rates
      .map((rate) => {
        const cents = Math.round(parseFloat(rate.price) * 100);
        const price = cents === 0 ? s.free : money(cents);
        const days = rate.delivery_days ?? [];
        const eta = days.length
          ? `<small>${(s.deliveryEstimate ?? '').replace(
              '[days]',
              days[0] === days[days.length - 1] ? String(days[0]) : `${days[0]}–${days[days.length - 1]}`
            )}</small>`
          : '';
        return `<div class="pp-ship__row"><span>${rate.name}${eta}</span><strong>${price}</strong></div>`;
      })
      .join('');
    if (!this.results) return;
    this.results.classList.remove('is-error');
    this.results.innerHTML = `<p class="pp-ship__for">${(s.resultsFor ?? '').replace('[location]', location)}</p>${rows}`;
    this.results.hidden = false;
  }
}
define('pp-shipping-calc', PpShippingCalc);

/* ==========================================================================
   Página de produto: variantes, compra, barra fixa
   ========================================================================== */

class PpProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[data-variants]');
    this.variants = json ? JSON.parse(json.textContent || '[]') : [];
    this.form = this.querySelector('form[data-product-form]');
    this.idInput = this.form?.querySelector('[name="id"]');

    this.querySelectorAll('[data-option-index]').forEach((el) => el.addEventListener('change', this.#onOption));
    this.form?.addEventListener('submit', this.#onSubmit);
    document.querySelectorAll(`[data-sticky-for="${this.id}"] [data-sticky-add]`).forEach((btn) =>
      btn.addEventListener('click', this.#onStickyAdd)
    );
    this.querySelectorAll('[data-thumb]').forEach((thumb) => thumb.addEventListener('click', this.#onThumb));
    this.#initGallery();

    this.#observeStickyBar();
  }

  #selectedOptions() {
    return [...this.querySelectorAll('[data-option-index]')]
      .filter((el) => !(el instanceof HTMLInputElement) || el.checked)
      .sort((a, b) => Number(a.getAttribute('data-option-index')) - Number(b.getAttribute('data-option-index')))
      .map((el) => (el instanceof HTMLInputElement || el instanceof HTMLSelectElement ? el.value : ''));
  }

  #onOption = () => {
    const options = this.#selectedOptions();
    const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    this.#update(variant);
  };

  /** @param {any} variant */
  #update(variant) {
    const button = this.querySelector('[data-add-button]');
    const label = button?.querySelector('[data-add-label]');
    const s = strings();

    if (!variant) {
      if (button instanceof HTMLButtonElement) button.disabled = true;
      if (label) label.textContent = s.unavailable ?? '';
      return;
    }

    if (this.idInput instanceof HTMLInputElement) this.idInput.value = String(variant.id);
    if (button instanceof HTMLButtonElement) button.disabled = !variant.available;
    if (label) label.textContent = variant.available ? s.buy ?? '' : s.soldOut ?? '';

    const scopes = [this, ...document.querySelectorAll(`[data-sticky-for="${this.id}"]`)];
    scopes.forEach((scope) => {
      scope.querySelectorAll('[data-unit-price]').forEach((box) => {
        box.setAttribute('data-unit-price', String(variant.price));
        box.setAttribute('data-unit-compare', String(variant.compare_at_price > variant.price ? variant.compare_at_price : 0));
      });
      scope.querySelectorAll('[data-compare-price]').forEach((el) => {
        el.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      });
      updateQuantityPreview(scope);
    });
    this.querySelectorAll('[data-sku]').forEach((el) => (el.textContent = variant.sku ?? ''));
    this.querySelectorAll('[data-barcode]').forEach((el) => (el.textContent = variant.barcode ?? ''));

    if (variant.featured_media) {
      const thumb = this.querySelector(`[data-thumb="${variant.featured_media.id}"]`);
      if (thumb instanceof HTMLElement) thumb.click();
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    await this.#add(this.querySelector('[data-add-button]'));
  };

  #onStickyAdd = async (event) => {
    const sticky = event.currentTarget instanceof Element ? event.currentTarget.closest('[data-sticky-for]') : null;
    const qty = sticky?.querySelector('pp-qty');
    const mainQty = this.querySelector('pp-qty');
    if (qty instanceof PpQty && mainQty instanceof PpQty) mainQty.value = qty.value;
    await this.#add(event.currentTarget);
  };

  /** @param {Element | null} button */
  async #add(button) {
    if (!(this.idInput instanceof HTMLInputElement) || !(button instanceof HTMLElement)) return;
    if (button.classList.contains('is-loading')) return;
    const qty = this.querySelector('pp-qty');
    const quantity = qty instanceof PpQty ? qty.value : 1;
    const error = this.querySelector('[data-add-error]');
    if (error) error.textContent = '';
    button.classList.add('is-loading');
    try {
      await addToCart(this.idInput.value, quantity, this);
    } catch (e) {
      if (error) error.textContent = e instanceof Error ? e.message : String(e);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /** Slide da galeria: setas, contador e miniaturas acompanham a rolagem. */
  #initGallery() {
    const main = this.querySelector('.pp-gallery__main');
    if (!(main instanceof HTMLElement)) return;
    const items = [...main.querySelectorAll('.pp-gallery__item')];
    if (items.length < 2) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#goTo(this.#galleryIndex() - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#goTo(this.#galleryIndex() + 1));

    let frame = 0;
    main.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#syncGallery());
      },
      { passive: true }
    );
    this.#syncGallery();
  }

  #galleryIndex() {
    const main = this.querySelector('.pp-gallery__main');
    const first = main?.querySelector('.pp-gallery__item');
    if (!(main instanceof HTMLElement) || !(first instanceof HTMLElement)) return 0;
    const step = first.offsetWidth + 8;
    return Math.round(main.scrollLeft / step);
  }

  /** @param {number} index */
  #goTo(index) {
    const main = this.querySelector('.pp-gallery__main');
    const items = main ? [...main.querySelectorAll('.pp-gallery__item')] : [];
    const target = items[Math.max(0, Math.min(items.length - 1, index))];
    if (main instanceof HTMLElement && target instanceof HTMLElement) {
      main.scrollTo({ left: target.offsetLeft - main.offsetLeft, behavior: 'smooth' });
    }
  }

  #syncGallery() {
    const items = [...this.querySelectorAll('.pp-gallery__item')];
    const index = Math.max(0, Math.min(items.length - 1, this.#galleryIndex()));
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= items.length - 1;

    const id = items[index]?.getAttribute('data-media-id');
    const thumbs = this.querySelector('.pp-gallery__thumbs');
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      const active = thumb.getAttribute('data-thumb') === id;
      thumb.setAttribute('aria-current', String(active));
      if (active && thumbs instanceof HTMLElement && thumb instanceof HTMLElement) {
        const left = thumb.offsetLeft - thumbs.offsetLeft;
        if (left < thumbs.scrollLeft || left + thumb.offsetWidth > thumbs.scrollLeft + thumbs.clientWidth) {
          thumbs.scrollTo({ left: left - 8, behavior: 'smooth' });
        }
      }
    });
  }

  /** @param {Event} event */
  #onThumb = (event) => {
    const thumb = event.currentTarget;
    if (!(thumb instanceof Element)) return;
    const index = [...this.querySelectorAll('.pp-gallery__item')].findIndex(
      (item) => item.getAttribute('data-media-id') === thumb.getAttribute('data-thumb')
    );
    if (index >= 0) this.#goTo(index);
  };

  #observeStickyBar() {
    const bar = document.querySelector(`[data-sticky-for="${this.id}"]`);
    const anchor = this.querySelector('[data-add-button]');
    if (!bar || !anchor) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        document.body.classList.toggle('pp-sticky-atc-visible', passed);
      },
      { threshold: 0 }
    );
    observer.observe(anchor);
  }
}
define('pp-product', PpProduct);

/* ==========================================================================
   Esconde cards sem foto que chegam via apps/AJAX e ajusta carrosséis vazios
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-pp-hide-if-empty]').forEach((el) => {
    if (!el.querySelector('.pp-card')) el.closest('.shopify-section')?.setAttribute('hidden', '');
  });
});

/* ==========================================================================
   Seções carregadas sob demanda (produtos relacionados)
   ========================================================================== */

class PpLazySection extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.querySelector('.pp-card')) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  async #load() {
    try {
      const html = await fetch(this.dataset.url ?? '').then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('pp-lazy-section');
      if (fresh && fresh.querySelector('.pp-card')) this.innerHTML = fresh.innerHTML;
      else this.hidden = true;
    } catch {
      this.hidden = true;
    }
  }
}
define('pp-lazy-section', PpLazySection);

/* ==========================================================================
   Produtos complementares (página de produto)
   ========================================================================== */

class PpRecommendations extends HTMLElement {
  async connectedCallback() {
    if (!this.dataset.url) return;
    try {
      const html = await fetch(this.dataset.url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.pp-recs');
      if (!content || !content.querySelector('li')) return;
      const title = document.createElement('p');
      title.className = 'pp-recs__heading';
      title.textContent = this.dataset.title ?? '';
      this.replaceChildren(title, content);
    } catch {
      /* sem recomendações */
    }
  }
}
define('pp-recommendations', PpRecommendations);

/* ==========================================================================
   Sugestões no carrinho lateral
   ========================================================================== */

let cartRecsTimer = 0;
const fillCartRecs = () => {
  clearTimeout(cartRecsTimer);
  cartRecsTimer = window.setTimeout(async () => {
    const holder = document.querySelector('[data-pp-cart-recs]');
    if (!(holder instanceof HTMLElement) || holder.childElementCount || !holder.dataset.productId) return;
    if (holder.dataset.loading === holder.dataset.productId) return;
    holder.dataset.loading = holder.dataset.productId;
    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const html = await fetch(
        `${root}recommendations/products?product_id=${holder.dataset.productId}&limit=10&section_id=pp-cart-recommendations`
      ).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.pp-cart-recs-content');
      const current = document.querySelector('[data-pp-cart-recs]');
      if (content && current instanceof HTMLElement && !current.childElementCount) current.append(content);
    } catch {
      /* sem sugestões */
    }
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  fillCartRecs();
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer) new MutationObserver(fillCartRecs).observe(drawer, { childList: true, subtree: true });
});

/* ==========================================================================
   Coleção/busca: carregar mais (com reposição quando há poucos itens
   visíveis) e filtros
   ========================================================================== */

class PpLoadMore extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const visible = Number(this.dataset.visible) || 0;
    if (this.dataset.next && visible < Number(this.dataset.minVisible || 0)) this.#fill();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.pp-load-more__btn') : null;
    if (!link) return;
    event.preventDefault();
    this.#loadNext();
  };

  /** Carrega páginas seguidas até ter itens suficientes na grade. */
  async #fill() {
    const min = Number(this.dataset.minVisible || 0);
    const grid = this.#grid();
    while (this.dataset.next && (grid?.children.length ?? 0) < min) {
      const ok = await this.#loadNext(true);
      if (!ok) break;
    }
  }

  #grid() {
    return this.closest('.pp-results__main')?.querySelector('[data-results-grid]') ?? null;
  }

  /** @param {boolean} [silent] */
  async #loadNext(silent = false) {
    if (this.#busy || !this.dataset.next) return false;
    this.#busy = true;
    const skeleton = this.querySelector('.pp-load-more__skeleton');
    skeleton?.removeAttribute('hidden');
    const btn = this.querySelector('.pp-load-more__btn');
    btn?.classList.add('is-loading');
    try {
      const html = await fetch(this.dataset.next).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-results-grid]');
      const nextMore = doc.querySelector('pp-load-more');
      let grid = this.#grid();
      if (nextGrid) {
        if (!grid) {
          grid = nextGrid.cloneNode(false);
          if (grid instanceof Element) this.before(grid);
        }
        grid?.append(...nextGrid.children);
      }
      const nextUrl = nextMore instanceof HTMLElement ? nextMore.dataset.next : undefined;
      if (nextUrl) this.dataset.next = nextUrl;
      else delete this.dataset.next;

      const count = nextMore?.querySelector('.pp-load-more__count');
      const ownCount = this.querySelector('.pp-load-more__count');
      if (count && ownCount) ownCount.textContent = count.textContent;
      if (!this.dataset.next) {
        ownCount?.remove();
        btn?.remove();
      }
      if (!silent && this.dataset.next) window.history.replaceState({}, '', window.location.href);
      return true;
    } catch {
      return false;
    } finally {
      skeleton?.setAttribute('hidden', '');
      btn?.classList.remove('is-loading');
      this.#busy = false;
    }
  }
}
define('pp-load-more', PpLoadMore);

document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-filters-toggle]') : null;
  if (!toggle) return;
  const id = toggle.getAttribute('aria-controls') ?? toggle.closest('.pp-filters')?.id;
  const panel = id ? document.getElementById(id) : toggle.closest('.pp-filters');
  if (!panel) return;
  const open = !panel.classList.contains('is-open');
  panel.classList.toggle('is-open', open);
  document.body.classList.toggle('pp-drawer-lock', open && window.innerWidth < 1024);
});

document.addEventListener('change', (event) => {
  const form = event.target instanceof Element ? event.target.closest('[data-filters-form]') : null;
  if (!(form instanceof HTMLFormElement)) return;
  const params = new URLSearchParams();
  new FormData(form).forEach((value, key) => {
    if (String(value).trim() !== '') params.append(key, String(value));
  });
  window.location.search = params.toString();
});
